import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Grid } from '@wegroup/design-system';

// Components
import SelectionItem from './SelectionItem';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  pushToSelectedRiskObjects,
  popFromSelectedRiskObjects,
} from '../../store/redux/slices/selectionSlice';

// Types
import { RootState } from '../../store/redux/reducers/rootReducer';
import SelectAllContainer from './SelectAllContainer';
import { useAffiliations, useBrokerAffiliations } from './SelectionQueries';
import { uniq } from 'lodash';
import { RiskObject } from '../../types/SelectionTypes';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  riskObjects: RiskObject[];
}

const RiskObjectSelection: React.FC<React.PropsWithChildren<Props>> = ({
  riskObjects,
}) => {
  const { t } = useTranslation();

  const { selectedRiskObjects } = useSelector(
    (state: RootState) => state.selection,
  );
  const dispatch = useDispatch();

  const { data: affiliations } = useAffiliations();
  const { data: brokerAffiliations } = useBrokerAffiliations();

  const enabledAffiliations = affiliations?.items.filter(
    ({ affiliation_info }) =>
      brokerAffiliations?.items
        .map(({ affiliation }) => affiliation?.name)
        .includes(affiliation_info?.name),
  );

  const enabledRiskObjects: string[] = isSocialLoginUrl
    ? riskObjects.map(({ name }) => name)
    : uniq(
        enabledAffiliations
          ?.map(({ affiliation_info }) => affiliation_info.insurances)
          .flat(),
      );

  const handleOnSelected = (name: string) => {
    if (selectedRiskObjects.includes(name))
      return dispatch(popFromSelectedRiskObjects(name));
    return dispatch(pushToSelectedRiskObjects(name));
  };

  const isAllSelected = enabledRiskObjects.every((name) =>
    selectedRiskObjects.includes(name),
  );

  const handleOnSelectAll = () => {
    if (isAllSelected)
      dispatch(
        popFromSelectedRiskObjects(selectedRiskObjects.map((name) => name)),
      );
    else
      dispatch(
        pushToSelectedRiskObjects(
          enabledRiskObjects.filter(
            (name) => !selectedRiskObjects.includes(name),
          ),
        ),
      );
  };

  return (
    <Grid
      templateColumns={{
        base: '100%',
        tabletS: 'repeat(2, 1fr)',
        tablet: 'repeat(auto-fill, 22rem)',
      }}
      gridGap="6"
      justifyContent="center"
      data-test-id="ProdFac_RiskObjectSelection"
    >
      <StyledSelectAllContainer
        onClick={handleOnSelectAll}
        allSelected={isAllSelected}
      />
      {riskObjects.map(({ name, description, imgSrc }) => (
        <StyledSelectionItem
          key={`selection_item_${name}`}
          title={t(name)}
          type={name}
          imgSrc={imgSrc}
          selected={selectedRiskObjects.includes(name)}
          onSelected={() => handleOnSelected(name)}
          disabled={!enabledRiskObjects.includes(name)}
          disableMessage={t(
            'You do not have affiliations that support this insurance type You can change this in your settings',
          )}
          disableSeverity="WARNING"
          data-test-id={`ADVISORY-CONVO_SELECTION-VIEW_SELECTION-ITEM_RISK-OBJECT-${name.toUpperCase()}`}
        >
          {t(description)}
        </StyledSelectionItem>
      ))}
    </Grid>
  );
};

const StyledSelectAllContainer = styled(SelectAllContainer)`
  grid-column: 1 / -1;
  padding-bottom: 0;
`;

const StyledSelectionItem = styled(SelectionItem)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export default RiskObjectSelection;
